/**
 * Realistic sample cellars for preview deploys and local demos, so a PR's
 * preview URL is testable on a phone without typing data in first.
 *
 * NEVER real data: this is only loaded into the in-memory fake storage when
 * the build sets VITE_SEED_DEMO=true (see CellarStoreContext.tsx and
 * CLAUDE.md "Data safety rules").
 *
 * Built by replaying the real domain change functions (recordPurchase,
 * drinkBottle, rateTasting) rather than hand-writing JSON, so the seed always
 * obeys the domain rules (identity, stock never negative, valid ratings) and
 * stops compiling if the data model changes.
 */
import {
  createEmptyCellar,
  drinkBottle,
  findWineByIdentity,
  rateTasting,
  recordPurchase,
  type Cellar,
  type WineDetails,
} from '../domain/cellar';
import { parseMonthValue, type MonthValue } from '../domain/months';
import { createProfile, type Profile } from '../domain/profile';

/** Fixed "today" for the seed, so every month below is in the past whatever the real date. */
const SEED_NOW: MonthValue = { year: 2026, month: 6 };

interface SeedPurchase {
  quantity: number;
  priceEuros: number;
  on: string;
}

interface SeedTasting {
  on: string;
  rating?: number;
  notes?: string;
}

interface SeedWine {
  wine: WineDetails;
  purchases: SeedPurchase[];
  tastings?: SeedTasting[];
}

/**
 * Drink-by dates chosen so a preview in autumn 2026 shows every colour step (User Story 6):
 * Weinbach 2019 (2028) step 1, Tignanello (2027) step 2, Beaucastel (2027) step 3 then 4 from
 * 30 June 2027, Tempier (2026) step 4 "à boire bientôt", Muga (2025) step 5 "dépassé",
 * Zind-Humbrecht (2035) no tint yet. The steps move on with the real date.
 */
const OWNER_WINES: SeedWine[] = [
  {
    wine: {
      domaine: 'Domaine Weinbach',
      cepage: 'Riesling',
      parcelle: 'Schlossberg',
      annee: 2019,
      pays: 'FR',
      region: 'Alsace',
      typeDeVin: 'blanc',
      drinkUntilYear: 2028,
    },
    purchases: [
      { quantity: 6, priceEuros: 38, on: '2021-05' },
      { quantity: 3, priceEuros: 41, on: '2023-04' },
    ],
    tastings: [
      { on: '2022-01', rating: 3.5, notes: 'Encore fermé, belle acidité.' },
      { on: '2023-12', rating: 4, notes: 'S’ouvre, notes de pierre à fusil.' },
      { on: '2025-11', rating: 4.5, notes: 'Superbe, agrumes confits et minéralité.' },
    ],
  },
  {
    wine: {
      domaine: 'Domaine Weinbach',
      cepage: 'Riesling',
      parcelle: 'Schlossberg',
      annee: 2021,
      pays: 'FR',
      region: 'Alsace',
      typeDeVin: 'blanc',
    },
    purchases: [{ quantity: 6, priceEuros: 42, on: '2023-04' }],
  },
  {
    wine: {
      domaine: 'Domaine Zind-Humbrecht',
      cepage: 'Gewurztraminer',
      parcelle: 'Clos Windsbuhl',
      annee: 2017,
      pays: 'FR',
      region: 'Alsace',
      typeDeVin: 'vendanges_tardives',
      drinkUntilYear: 2035,
    },
    purchases: [{ quantity: 3, priceEuros: 65, on: '2020-10' }],
    tastings: [{ on: '2024-12', rating: 5, notes: 'Litchi, rose, finale interminable.' }],
  },
  {
    wine: {
      domaine: 'Château de Beaucastel',
      cepage: 'Grenache, Mourvèdre, Syrah',
      annee: 2016,
      pays: 'FR',
      region: 'Vallée du Rhône',
      typeDeVin: 'rouge',
      drinkUntilYear: 2027,
    },
    purchases: [{ quantity: 6, priceEuros: 72, on: '2019-03' }],
    tastings: [
      { on: '2021-06', rating: 4 },
      { on: '2023-02', rating: 4.5, notes: 'Garrigue, cuir, tanins fondus.' },
      { on: '2025-09', rating: 4, notes: 'Commence à décliner légèrement.' },
      { on: '2026-02' },
    ],
  },
  {
    wine: {
      domaine: 'Domaine Jamet',
      cepage: 'Syrah',
      parcelle: 'Côte-Rôtie',
      annee: 2018,
      pays: 'FR',
      region: 'Vallée du Rhône',
      typeDeVin: 'rouge',
    },
    purchases: [{ quantity: 2, priceEuros: 95, on: '2021-11' }],
    tastings: [
      { on: '2023-12', rating: 4.5, notes: 'Olive noire, violette.' },
      { on: '2025-12', rating: 5 },
    ],
  },
  {
    wine: {
      domaine: 'Domaine Tempier',
      cepage: 'Mourvèdre',
      annee: 2024,
      pays: 'FR',
      region: 'Provence',
      typeDeVin: 'rose',
      drinkUntilYear: 2026,
    },
    purchases: [{ quantity: 12, priceEuros: 29, on: '2025-05' }],
    tastings: [
      { on: '2025-07', rating: 4, notes: 'Pêche blanche, épices douces.' },
      { on: '2025-08', rating: 3.5 },
    ],
  },
  {
    wine: {
      domaine: 'Domaine Leflaive',
      cepage: 'Chardonnay',
      parcelle: 'Clavoillon',
      annee: 2020,
      pays: 'FR',
      region: 'Bourgogne',
      typeDeVin: 'blanc',
    },
    purchases: [{ quantity: 1, priceEuros: 120, on: '2022-09' }],
    tastings: [{ on: '2025-04', rating: 4.5, notes: 'Beurre noisette, tension.' }],
  },
  {
    wine: {
      domaine: 'Marchesi Antinori',
      cepage: 'Sangiovese',
      parcelle: 'Tignanello',
      annee: 2019,
      pays: 'IT',
      region: 'Toscana',
      typeDeVin: 'rouge',
      drinkUntilYear: 2027,
    },
    purchases: [{ quantity: 3, priceEuros: 110, on: '2022-12' }],
    tastings: [{ on: '2024-12', rating: 3.5, notes: 'Boisé encore marqué.' }],
  },
  {
    wine: {
      domaine: 'Bodegas Muga',
      cepage: 'Tempranillo',
      annee: 2018,
      pays: 'ES',
      region: 'Rioja',
      typeDeVin: 'rouge',
      drinkUntilYear: 2025,
    },
    purchases: [{ quantity: 6, priceEuros: 22, on: '2023-02' }],
  },
  {
    wine: {
      domaine: 'Domaine Huet',
      cepage: 'Chenin blanc',
      parcelle: 'Le Haut-Lieu',
      annee: 2015,
      pays: 'FR',
      region: 'Loire',
      typeDeVin: 'vendanges_tardives',
    },
    purchases: [{ quantity: 2, priceEuros: 48, on: '2018-08' }],
    tastings: [
      { on: '2020-12', rating: 4 },
      { on: '2024-12', rating: 3.5, notes: 'Plus évolué que prévu.' },
    ],
  },
];

const MEMBER_WINES: SeedWine[] = [
  {
    wine: {
      domaine: 'Domaine Weinbach',
      cepage: 'Riesling',
      parcelle: 'Schlossberg',
      annee: 2019,
      pays: 'FR',
      region: 'Alsace',
      typeDeVin: 'blanc',
    },
    purchases: [{ quantity: 2, priceEuros: 38, on: '2021-05' }],
  },
  {
    wine: {
      domaine: 'Domaine Gauby',
      cepage: 'Grenache blanc',
      annee: 2022,
      pays: 'FR',
      region: 'Roussillon',
      typeDeVin: 'blanc',
    },
    purchases: [{ quantity: 4, priceEuros: 31, on: '2024-06' }],
    tastings: [{ on: '2025-01', rating: 4 }],
  },
];

function buildCellar(wines: SeedWine[]): Cellar {
  let cellar = createEmptyCellar();
  for (const seed of wines) {
    for (const p of seed.purchases) {
      cellar = recordPurchase(
        {
          wine: seed.wine,
          quantity: p.quantity,
          unitPriceCents: Math.round(p.priceEuros * 100),
          purchasedOn: parseMonthValue(p.on),
        },
        SEED_NOW,
      )(cellar);
    }
    const wineId = findWineByIdentity(cellar, seed.wine)!.id;
    for (const [index, t] of (seed.tastings ?? []).entries()) {
      const tastingId = `seed-${wineId}-${index}`;
      cellar = drinkBottle(
        { wineId, tastedOn: parseMonthValue(t.on), tastingId },
        SEED_NOW,
      )(cellar);
      if (t.rating !== undefined) {
        cellar = rateTasting({
          wineId,
          tastingId,
          rating: t.rating,
          ...(t.notes !== undefined ? { notes: t.notes } : {}),
        })(cellar);
      }
    }
  }
  return cellar;
}

/** Email of the sample member; sign in with it on a preview to see a second, separate cellar. */
export const SEED_MEMBER_EMAIL = 'membre@example.com';

/** The sample member's cellar name (owner, 2026-10-08: set under Réglages), "Cave de Papa". */
export const SEED_MEMBER_NAME = 'Cave de Papa';

/**
 * Storage objects to preload into the fake storage, keyed by object path.
 * `cellarIdFor` maps an email to its cellar id (the fake session's rule).
 */
export function buildSeedObjects(
  ownerEmail: string,
  cellarIdFor: (email: string) => string,
): Record<string, Cellar | Profile> {
  const owner = cellarIdFor(ownerEmail);
  const member = cellarIdFor(SEED_MEMBER_EMAIL);
  return {
    [`cellars/${owner}/cellar.json`]: buildCellar(OWNER_WINES),
    [`cellars/${owner}/profile.json`]: createProfile(ownerEmail),
    [`cellars/${member}/cellar.json`]: buildCellar(MEMBER_WINES),
    [`cellars/${member}/profile.json`]: {
      ...createProfile(SEED_MEMBER_EMAIL),
      name: SEED_MEMBER_NAME,
    },
  };
}
